import { Instance, applySnapshot, flow, types } from 'mobx-state-tree';

import { completeGradeBook } from '@common/api/gradebooks';
import { getStudentIndivLesson, getStudentLesson } from '@common/api/studentLesson';
import RubricGroup from '@common/models/Rubrics/RubricGroup';
import RubricLevel from '@common/models/Rubrics/RubricLevel';

import GradeBook from '../GradeBooks/GradeBook';
import Lesson from '../Lessons/Lesson';
import ScheduleLesson from '../Schedules/ScheduleLesson';
import StudentLessonGrade from './Grade';
import NextScheduleLesson from './NextScheduleLesson';
import StudentLessonTeacher from './Teacher';

const StudentLesson = types
	.model('StudentLesson', {
		gradebook: types.maybe(types.maybeNull(GradeBook)), // @TODO: GradeBook model
		lesson_id: types.frozen(),
		lesson_info: types.maybe(Lesson),
		schedule_lesson_id: types.frozen(),
		is_ai_tutor_available: types.maybe(types.boolean),
		student_id: types.maybe(types.number),
		schedule_lesson: types.maybeNull(ScheduleLesson),
		grades: types.optional(types.array(StudentLessonGrade), []),
		teachers: types.optional(types.array(StudentLessonTeacher), []),
		schedule_program: types.frozen(), // @TODO: Model
		next_schedule_lesson: types.maybeNull(NextScheduleLesson),
		rubricUnions: types.optional(types.array(RubricGroup), []),
		rubricLevels: types.optional(types.array(RubricLevel), []),
	})
	.volatile(
		(): {
			loading: boolean;
			lessonStartDate: string | null;
		} => ({
			loading: true,
			lessonStartDate: null,
		}),
	)
	.views((self) => ({
		get canBeCompleted() {
			const LESSON_TYPES = [3];
			if (LESSON_TYPES.includes(self.lesson_info?.lessonType?.id)) {
				return true;
			}
			return !self.lesson_info.homework?.is_enabled;
		},
		get gradesWithComment() {
			return self.grades?.filter((grade) => grade.comment || grade.reject_comment);
		},
	}))
	.actions((self) => ({
		fetch: flow(function* () {
			try {
				self.loading = true;
				const result = yield getStudentLesson(self.schedule_lesson_id, {
					...(self.student_id && { student_id: self.student_id }),
				});
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		fetchIndiv: flow(function* (query: any) {
			try {
				self.loading = true;
				const result = yield getStudentIndivLesson(self.schedule_lesson_id, query);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		complete: flow(function* () {
			try {
				self.loading = true;
				yield completeGradeBook(self.gradebook.id); // @TODO: applySnapshot
			} finally {
				self.loading = false;
			}
		}),
		setLessonStartDate: (date: string) => {
			self.lessonStartDate = date;
		},
	}));

export default StudentLesson;

export interface IStudentLessonModel extends Instance<typeof StudentLesson> {}
